/**
 * The screen a person was on when they chose to sign in. The sign-in leaves the app for Keycloak
 * and comes back to `/`, so the hash is kept in `sessionStorage` (this tab only) and put back after
 * the session answers. Only the hash is kept, never a URL: the value comes from the address bar,
 * so it is checked on the way in and on the way out, and `App` sets `location.hash` with it, never
 * `location.href`.
 */
export const RETURN_HASH_KEY = 'ops-console.return-hash';
const MAX_LENGTH = 512;
/** A route hash: `#` and then the characters RFC 3986 allows in a fragment (a document id may have `(1)` or `,`). */
const ROUTE_HASH = /^#[A-Za-z0-9\-._~%!$&'()*+,;=:@/?]*$/;
/** The callback's own hash (`#signin-error=...`) is a result, not a screen. */
const SIGNIN_ERROR_HASH = /^#signin-error=/;

export function isRouteHash(hash: string): boolean {
  return hash.length > 1 && hash.length <= MAX_LENGTH && ROUTE_HASH.test(hash) && !SIGNIN_ERROR_HASH.test(hash);
}

/** Keeps `hash` for after sign-in. A hash that is not a route clears the old one. A `#signin-error` hash keeps it, so a second try still lands on the first screen. */
export function saveReturnHash(hash: string): void {
  try {
    if (SIGNIN_ERROR_HASH.test(hash)) return;
    if (isRouteHash(hash)) sessionStorage.setItem(RETURN_HASH_KEY, hash);
    else sessionStorage.removeItem(RETURN_HASH_KEY);
  } catch {
    // Storage is blocked (a private window): the person lands on Processing, as before.
  }
}

/** The kept hash, once. `null` for none, or for a value that is not a route hash. */
export function takeReturnHash(): string | null {
  try {
    const hash = sessionStorage.getItem(RETURN_HASH_KEY);
    sessionStorage.removeItem(RETURN_HASH_KEY);
    return hash !== null && isRouteHash(hash) ? hash : null;
  } catch {
    return null;
  }
}
